// A world NPC: a Tripo-rigged avatar looping one library move, or walking
// around its spot. Rendered at the NPC's feet (the parent group carries the
// placed position and rotation).
//
// Walking is deterministic from the clock and the NPC's id, so every visitor
// sees the same NPC in the same place without any network traffic: time is cut
// into fixed slots; in slot k the NPC walks from point k to point k+1 (seeded
// random spots inside its radius), then idles out the rest of the slot.
import React, { Suspense, useEffect, useMemo, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { useGLTF, Text, Billboard } from '@react-three/drei';
import { clone } from 'three/examples/jsm/utils/SkeletonUtils';
import * as THREE from 'three';
import { captureRest, fitLibraryClip } from '../../avatar/libraryRetarget';
import { LIBRARY_SOURCE_MODEL } from '../../config/emotes';
import { clipUrl, npcAnim, type NpcConfig } from '../../config/npc';
import FloatingNameTagWithEvents from '../FloatingNameTagWithEvents';
import { useNpcTalk, npcBodies } from './npcTalkStore';

const WALK_SPEED = 1.1;
const JOG_SPEED = 2.6;
const MAX_PAUSE = 5;

function hash(s: string): number {
  let h = 2166136261;
  for (const c of s) h = Math.imul(h ^ c.charCodeAt(0), 16777619);
  return h >>> 0;
}
/** Seeded spot k inside a disc of the given radius (k may be any integer). */
function spot(seed: number, k: number, radius: number): [number, number] {
  const a = hash(`${seed}:${k}:a`) / 4294967296;
  const r = Math.sqrt(hash(`${seed}:${k}:r`) / 4294967296) * radius;
  return [Math.cos(a * Math.PI * 2) * r, Math.sin(a * Math.PI * 2) * r];
}

interface Props {
  url: string;
  name: string;
  anim: string;
  radius: number;
  /** Stable per NPC (its primitive id) so walking paths differ between NPCs. */
  seed: string;
  /** false in the editor: animate in place so the gizmo stays put. */
  roam?: boolean;
  showName?: boolean;
}

const NpcModel: React.FC<Props> = ({ url, name, anim, radius, seed, roam = true, showName = true }) => {
  const gltf = useGLTF(url);
  const skeleton = useGLTF(LIBRARY_SOURCE_MODEL);
  const def = npcAnim(anim);
  const moving = !!def.moves;
  const mainPreset = !moving ? def.id : anim === 'jog' ? 'run' : 'walk';
  const mainGltf = useGLTF(clipUrl(mainPreset));
  const idleGltf = useGLTF(clipUrl('idle'));

  const model = useMemo(() => {
    const c = clone(gltf.scene);
    c.traverse((o: any) => {
      if (o.isMesh) { o.castShadow = true; o.frustumCulled = false; }
    });
    return c;
  }, [gltf.scene]);
  const rest = useMemo(() => captureRest(gltf.scene), [gltf.scene]);

  const mixer = useMemo(() => new THREE.AnimationMixer(model), [model]);
  const actions = useMemo(() => {
    const fit = (src: THREE.Group & { animations?: THREE.AnimationClip[] } | any, label: string) => {
      const raw: THREE.AnimationClip | undefined = src.animations?.[0];
      if (!raw) return null;
      const c = fitLibraryClip(raw, skeleton.scene, rest);
      c.name = label;
      return mixer.clipAction(c);
    };
    return { main: fit(mainGltf, 'main'), idle: fit(idleGltf, 'idle') };
  }, [mixer, mainGltf, idleGltf, skeleton.scene, rest]);

  const state = useRef<'main' | 'idle' | null>(null);
  const play = (which: 'main' | 'idle') => {
    if (state.current === which) return;
    const next = actions[which] ?? actions.main;
    const prev = state.current ? actions[state.current] : null;
    if (!next) return;
    next.reset().setLoop(THREE.LoopRepeat, Infinity).fadeIn(0.25).play();
    if (prev && prev !== next) prev.fadeOut(0.25);
    state.current = which;
  };
  useEffect(() => {
    state.current = null;
    play(moving && roam ? 'idle' : 'main');
    // Offset each NPC's loop so a crowd doing the same move isn't in lockstep.
    if (actions.main) actions.main.time = (hash(seed) % 1000) / 1000 * actions.main.getClip().duration;
    return () => { mixer.stopAllAction(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actions, moving, roam]);

  const body = useRef<THREE.Group>(null);
  const yaw = useRef(0);
  const seedN = useMemo(() => hash(seed), [seed]);
  const { camera } = useThree();
  useEffect(() => {
    const g = body.current;
    if (!g || !roam) return;
    npcBodies.set(seed, g);
    return () => { if (npcBodies.get(seed) === g) npcBodies.delete(seed); };
  }, [seed, roam]);
  const _look = useMemo(() => new THREE.Vector3(), []);
  useFrame((_, dt) => {
    mixer.update(Math.min(dt, 0.1));
    const g = body.current;
    if (!g) return;
    // In conversation: stop where it is and turn to face the visitor.
    if (roam && useNpcTalk.getState().talkingTo === seed && g.parent) {
      if (moving) play('idle');
      camera.getWorldPosition(_look);
      g.parent.worldToLocal(_look);
      const want = Math.atan2(_look.x - g.position.x, _look.z - g.position.z);
      let d = want - g.rotation.y;
      d = Math.atan2(Math.sin(d), Math.cos(d));
      g.rotation.y += d * Math.min(1, dt * 6);
      yaw.current = g.rotation.y;
      return;
    }
    if (!moving || !roam || radius <= 0) {
      g.position.set(0, 0, 0);
      g.rotation.y = 0;
      if (moving) play('main'); // editor preview: walk on the spot
      return;
    }
    const speed = anim === 'jog' ? JOG_SPEED : WALK_SPEED;
    const slot = (radius * 2) / speed + MAX_PAUSE;
    const t = Date.now() / 1000;
    const k = Math.floor(t / slot);
    const s = t - k * slot;
    const a = spot(seedN, k, radius);
    const b = spot(seedN, k + 1, radius);
    const dx = b[0] - a[0], dz = b[1] - a[1];
    const walkFor = Math.hypot(dx, dz) / speed;
    // Start each slot with a short pause, then walk, then idle at the new spot.
    const pause = 0.4 + (hash(`${seedN}:${k}:p`) / 4294967296) * (slot - walkFor - 0.4);
    let x = a[0], z = a[1];
    if (s >= pause + walkFor) { x = b[0]; z = b[1]; play('idle'); }
    else if (s > pause) {
      const f = (s - pause) / walkFor;
      x = a[0] + dx * f; z = a[1] + dz * f;
      yaw.current = Math.atan2(dx, dz);
      play('main');
    } else play('idle');
    g.position.set(x, 0, z);
    // Turn smoothly toward the walking direction.
    let d = yaw.current - g.rotation.y;
    d = Math.atan2(Math.sin(d), Math.cos(d));
    g.rotation.y += d * Math.min(1, dt * 8);
  });

  return (
    <group ref={body} name="npc-body">
      <primitive object={model} />
      {showName && name && <FloatingNameTagWithEvents name={name} position={[0, 0, 0]} />}
    </group>
  );
};

/** Stand-in shown in the editor while the character is building (or if it failed). */
const NpcPlaceholder: React.FC<{ npc: NpcConfig; selected?: boolean }> = ({ npc, selected }) => {
  const failed = npc.status === 'failed';
  const ref = useRef<THREE.Mesh>(null);
  useFrame(({ clock }) => {
    const m = ref.current?.material as THREE.MeshStandardMaterial | undefined;
    if (m && !failed) m.opacity = 0.25 + Math.sin(clock.elapsedTime * 3) * 0.12;
  });
  return (
    <group>
      <mesh ref={ref} position={[0, 0.9, 0]}>
        <capsuleGeometry args={[0.28, 1.15, 6, 16]} />
        <meshStandardMaterial color={failed ? '#ff6b6b' : selected ? '#ffd166' : '#00c2b3'} transparent opacity={0.35} depthWrite={false} />
      </mesh>
      <Billboard position={[0, 2.05, 0]}>
        <Text fontSize={0.11} color={failed ? '#ffb4b4' : '#eaf6f8'} anchorX="center" outlineWidth={0.006} outlineColor="#04131b" font="/fonts/outfit-600.woff">
          {failed ? `${npc.name}: build failed` : `Building ${npc.name}…`}
        </Text>
      </Billboard>
    </group>
  );
};

/** An NPC at its feet: the character when ready; in the editor, a placeholder until then. */
const NpcAvatar: React.FC<{ id: string; npc: NpcConfig; editor?: boolean; selected?: boolean }> = ({ id, npc, editor, selected }) => {
  if (!npc.avatarUrl || npc.status !== 'ready') return editor ? <NpcPlaceholder npc={npc} selected={selected} /> : null;
  return (
    <Suspense fallback={editor ? <NpcPlaceholder npc={{ ...npc, status: 'building' }} selected={selected} /> : null}>
      <NpcModel url={npc.avatarUrl} name={npc.name} anim={npc.anim} radius={npc.radius} seed={id} roam={!editor} />
    </Suspense>
  );
};

export default NpcAvatar;
